import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('idle');   // idle | success
  const [shakeKey, setShakeKey] = useState(0);  // retrigger shake on repeat errors

  const update = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading || phase === 'success') return;
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      setPhase('success');                 // play exit animation
      setTimeout(() => navigate('/'), 850); // then enter the app
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
      setShakeKey(k => k + 1);             // shake the card
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #1a2744 0%, #2d3f6b 60%, #3d5499 100%)' }}>

      <style>{`
        @keyframes logoGlow {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 0.7;  transform: scale(1.15); }
        }
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cardShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-10px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(4px); }
          75% { transform: translateX(-2px); }
        }
        @keyframes cardOut {
          to { opacity: 0; transform: translateY(-16px) scale(0.96); filter: blur(4px); }
        }
        @keyframes wipeExpand {
          from { transform: translate(-50%, -50%) scale(0); opacity: 1; }
          to   { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes checkPop {
          0%   { transform: scale(0) rotate(-30deg); opacity: 0; }
          60%  { transform: scale(1.25) rotate(6deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes checkDraw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes btnShimmer {
          from { transform: translateX(-100%) skewX(-15deg); }
          to   { transform: translateX(250%) skewX(-15deg); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Glow blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[15%] w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #fb866d, transparent)' }} />
        <div className="absolute bottom-[15%] left-[5%] w-72 h-72 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #fb866d, transparent)' }} />
      </div>

      {/* ── Success wipe: coral circle expanding over the whole screen ── */}
      {phase === 'success' && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
          <div className="absolute left-1/2 top-1/2 rounded-full"
            style={{
              width: '250vmax', height: '250vmax',
              background: 'radial-gradient(circle, #fb866d 0%, #e8614a 55%, #1a2744 100%)',
              animation: 'wipeExpand 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards',
            }} />
          {/* checkmark pops in the middle of the wipe */}
          <div className="relative z-10" style={{ animation: 'checkPop 0.5s 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}>
            <svg width="96" height="96" viewBox="0 0 24 24" fill="none"
              stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" opacity="0.35" />
              <path d="M7 12.5 10.5 16 17 8.5"
                style={{ strokeDasharray: 24, strokeDashoffset: 24, animation: 'checkDraw 0.35s 0.5s ease-out forwards' }} />
            </svg>
          </div>
        </div>
      )}

      {/* ── Card ── */}
      <div key={shakeKey}
        className="relative w-[420px] max-w-[96vw] rounded-3xl p-12"
        style={{
          background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          animation: phase === 'success'
            ? 'cardOut 0.45s ease-in forwards'
            : shakeKey > 0
              ? 'cardShake 0.45s ease, cardIn 0s'
              : 'cardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
        }}>

        {/* Logo */}
        <div className="flex justify-center gap-3 mb-9">
          <div className="relative w-28 h-28 flex-shrink-0">
            <div className="absolute -inset-4 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(251,134,109,0.18) 45%, transparent 70%)',
                animation: 'logoGlow 3s ease-in-out infinite',
                filter: 'blur(10px)',
              }} />
            <img src="/logo.webp" alt="Delta"
              className="relative w-full h-full object-contain"
              style={{
                animation: 'logoFloat 4s ease-in-out infinite',
                filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.45)) drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
              }} />
          </div>
        </div>

        <h1 className="text-white text-3xl font-bold tracking-tight mb-1">Welcome back</h1>
        <p className="text-white/50 text-sm mb-7">Manage your workforce, smarter.</p>

        {/* Tabs */}
        <div className="flex gap-1 mb-7 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
          {['login', 'signup'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${tab === t ? 'bg-white shadow-sm' : 'text-white/60 hover:text-white/80'}`}
              style={tab === t ? { color: '#1a2744' } : {}}>
              {t === 'login' ? 'Sign In' : 'Sign Up'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {tab === 'signup' && (
            <div>
              <label className="block text-white/70 text-[11px] font-semibold uppercase tracking-wider mb-1.5">Full Name</label>
              <input name="name" placeholder="Your name" value={form.name} onChange={update} required
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-all"
                style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)' }}
                onFocus={e => e.target.style.borderColor = '#fb866d'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
              />
            </div>
          )}
          <div>
            <label className="block text-white/70 text-[11px] font-semibold uppercase tracking-wider mb-1.5">Email</label>
            <input name="email" type="email" placeholder="admin@delta.com" value={form.email} onChange={update} required
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)' }}
              onFocus={e => e.target.style.borderColor = '#fb866d'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
            />
          </div>
          <div>
            <label className="block text-white/70 text-[11px] font-semibold uppercase tracking-wider mb-1.5">Password</label>
            <input name="password" type="password" placeholder="••••••••" value={form.password} onChange={update} required
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)' }}
              onFocus={e => e.target.style.borderColor = '#fb866d'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
            />
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl text-sm"
              style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
              {error}
            </div>
          )}

          {/* Submit — shimmer while loading, spinner, press feedback */}
          <button type="submit" disabled={loading || phase === 'success'}
            className="relative w-full py-3 mt-1 rounded-xl text-white text-[15px] font-semibold overflow-hidden transition-all hover:-translate-y-0.5 active:scale-[0.98] disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #fb866d, #e8614a)',
              boxShadow: loading ? '0 0 24px rgba(251,134,109,0.6)' : '0 4px 16px rgba(251,134,109,0.35)',
            }}>
            {/* shimmer sweep while loading */}
            {loading && (
              <span className="absolute inset-y-0 w-1/3 pointer-events-none"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                  animation: 'btnShimmer 1.1s ease-in-out infinite',
                }} />
            )}
            <span className="relative flex items-center justify-center gap-2.5">
              {loading && (
                <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white inline-block"
                  style={{ animation: 'spin 0.7s linear infinite' }} />
              )}
              {phase === 'success' ? 'Welcome!' : loading ? 'Signing in...' : tab === 'login' ? 'Sign In' : 'Create Account'}
            </span>
          </button>
        </form>

        <p className="text-center mt-5 text-white/30 text-xs">Default: admin@delta.com / admin123</p>
      </div>
    </div>
  );
}